"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.equalSplitWindows = equalSplitWindows;
exports.clampWindow = clampWindow;
exports.shiftWindow = shiftWindow;
exports.parsePivotWindow = parsePivotWindow;
exports.formatPivotWindow = formatPivotWindow;
exports.personalWindow = personalWindow;
exports.windowDurationInMinutes = windowDurationInMinutes;
exports.availableSlots = availableSlots;
exports.freeRegions = freeRegions;
exports.nextAvailableWindow = nextAvailableWindow;
exports.snapWindowToNeighbors = snapWindowToNeighbors;
var time_1 = require("./time");
var WIRE_FORMAT = "YYYY-MM-DD HH:mm:ss";
var DISPLAY_FORMAT = "h:mm a";
/**
 * Partition the lesson envelope into sequential, equal-length slots, giving the
 * driver the first slot and observers the slots after. The final slot absorbs
 * any remainder minutes so the windows tile the envelope exactly.
 *
 * Returned windows are aligned to the input students array (index for index),
 * even though the time slots themselves are allocated driver-first.
 */
function equalSplitWindows(envelopeStart, sessionLengthMinutes, students) {
    var ordered = __spreadArray(__spreadArray([], students
        .map(function (student, index) { return ({ student: student, index: index }); })
        .filter(function (entry) { return entry.student.observer_or_driver === "driver"; }), true), students
        .map(function (student, index) { return ({ student: student, index: index }); })
        .filter(function (entry) { return entry.student.observer_or_driver !== "driver"; }), true);
    var windows = new Array(students.length);
    var count = ordered.length;
    if (count === 0) {
        return windows;
    }
    var slotLength = Math.floor(sessionLengthMinutes / count);
    var cursorMinutes = 0;
    ordered.forEach(function (entry, position) {
        var isLastSlot = position === count - 1;
        var length = isLastSlot
            ? sessionLengthMinutes - cursorMinutes
            : slotLength;
        windows[entry.index] = {
            start: (0, time_1.addMinutes)(envelopeStart, cursorMinutes),
            end: (0, time_1.addMinutes)(envelopeStart, cursorMinutes + length),
        };
        cursorMinutes += length;
    });
    return windows;
}
/**
 * Coerce a window so it sits inside the lesson envelope. If clamping leaves a
 * non-positive window, fall back to the full envelope. The returned `clamped`
 * flag tells the form whether to surface an inline notice.
 */
function clampWindow(start, end, envelopeStart, envelopeEnd) {
    var clampedStart = start;
    var clampedEnd = end;
    if (clampedStart < envelopeStart) {
        clampedStart = new Date(envelopeStart);
    }
    if (clampedEnd > envelopeEnd) {
        clampedEnd = new Date(envelopeEnd);
    }
    var clamped = clampedStart.getTime() !== start.getTime() ||
        clampedEnd.getTime() !== end.getTime();
    if (clampedEnd <= clampedStart) {
        return {
            start: new Date(envelopeStart),
            end: new Date(envelopeEnd),
            clamped: true,
        };
    }
    return { start: clampedStart, end: clampedEnd, clamped: clamped };
}
function shiftWindow(window, deltaMinutes) {
    return {
        start: (0, time_1.addMinutes)(window.start, deltaMinutes),
        end: (0, time_1.addMinutes)(window.end, deltaMinutes),
    };
}
/**
 * Parse the wire-format window the API returns on each pivot row. This is the
 * single place the "YYYY-MM-DD HH:mm:ss" contract lives.
 */
function parsePivotWindow(start, end) {
    return {
        start: (0, time_1.dateTimeStringToDate)(start),
        end: (0, time_1.dateTimeStringToDate)(end),
    };
}
function formatPivotWindow(window) {
    return {
        start: (0, time_1.formatDateTime)(window.start, WIRE_FORMAT),
        end: (0, time_1.formatDateTime)(window.end, WIRE_FORMAT),
    };
}
function personalWindow(drive, studentId) {
    var _a, _b;
    var student = (drive.students || []).find(function (s) { return s.id === studentId; });
    if (!student) {
        return null;
    }
    // The window arrives either flattened (admin edit) or under the pivot (raw API).
    var start = student.start || ((_a = student.pivot) === null || _a === void 0 ? void 0 : _a.start);
    var end = student.end || ((_b = student.pivot) === null || _b === void 0 ? void 0 : _b.end);
    if (!start || !end) {
        return null;
    }
    var window = parsePivotWindow(start, end);
    return {
        startTime: (0, time_1.formatDateTime)(window.start, DISPLAY_FORMAT),
        endTime: (0, time_1.formatDateTime)(window.end, DISPLAY_FORMAT),
    };
}
function windowDurationInMinutes(window) {
    return (0, time_1.differenceInMinutes)(window.end, window.start);
}
/**
 * Turn the drive's raw observation slots into a time-ordered, display-ready list
 * for the booking screen. A slot is `mine` when the current student already holds
 * it and `taken` when another student does; only the remaining slots are
 * bookable. Kept pure so it can be unit tested without the AngularJS controller.
 */
function availableSlots(slots, currentUserId) {
    return (slots || [])
        .map(function (slot) {
        var window = parsePivotWindow(slot.start, slot.end);
        var mine = slot.student_id === currentUserId;
        var taken = slot.student_id !== null && !mine;
        return {
            id: slot.id,
            start: window.start,
            end: window.end,
            label: (0, time_1.formatDateTime)(window.start, DISPLAY_FORMAT) +
                " – " +
                (0, time_1.formatDateTime)(window.end, DISPLAY_FORMAT),
            taken: taken,
            mine: mine,
        };
    })
        .sort(function (first, second) { return first.start.getTime() - second.start.getTime(); });
}
/**
 * The portions of the lesson envelope not covered by any occupied window, in
 * order. Used to place a new open slot without overlapping an existing one.
 */
function freeRegions(envelope, occupied) {
    var sorted = __spreadArray([], occupied, true).sort(function (first, second) { return first.start.getTime() - second.start.getTime(); });
    var regions = [];
    var cursor = envelope.start;
    sorted.forEach(function (slot) {
        if (slot.start.getTime() > cursor.getTime()) {
            regions.push({ start: new Date(cursor), end: new Date(slot.start) });
        }
        if (slot.end.getTime() > cursor.getTime()) {
            cursor = slot.end;
        }
    });
    if (cursor.getTime() < envelope.end.getTime()) {
        regions.push({ start: new Date(cursor), end: new Date(envelope.end) });
    }
    return regions;
}
/**
 * The first free gap a new open slot can occupy, or null when the lesson is
 * already fully covered. With no existing slots this returns the whole envelope.
 */
function nextAvailableWindow(envelope, existing) {
    var regions = freeRegions(envelope, existing);
    return regions.length > 0 ? regions[0] : null;
}
/**
 * Clamp the boundary the user just edited so the slot stays inside the envelope
 * and cannot cross into a neighboring slot. The opposite boundary is held fixed;
 * because the other slots are kept non-overlapping, the limit is exactly the
 * nearest neighbor's edge. `adjusted` reports whether the value had to move.
 */
function snapWindowToNeighbors(window, others, envelope, changed) {
    if (changed === "start") {
        var lowerBound = others
            .filter(function (other) { return other.end.getTime() <= window.end.getTime(); })
            .reduce(function (bound, other) {
            return other.end.getTime() > bound.getTime() ? other.end : bound;
        }, envelope.start);
        var start = window.start;
        if (start.getTime() < lowerBound.getTime()) {
            start = new Date(lowerBound);
        }
        if (start.getTime() > window.end.getTime()) {
            start = new Date(window.end);
        }
        return {
            start: start,
            end: window.end,
            adjusted: start.getTime() !== window.start.getTime(),
        };
    }
    var upperBound = others
        .filter(function (other) { return other.start.getTime() >= window.start.getTime(); })
        .reduce(function (bound, other) {
        return other.start.getTime() < bound.getTime() ? other.start : bound;
    }, envelope.end);
    var end = window.end;
    if (end.getTime() > upperBound.getTime()) {
        end = new Date(upperBound);
    }
    if (end.getTime() < window.start.getTime()) {
        end = new Date(window.start);
    }
    return {
        start: window.start,
        end: end,
        adjusted: end.getTime() !== window.end.getTime(),
    };
}
